import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';

import { SCUIDateRangerV2, SCUILoadingLinear, SCUINoRecordComponent } from '../../../atomic/atoms';
import SCUIDownloadButton from '../../../atomic/atoms/SCUIDownloadButton/SCUIDownloadButton';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';

import { StudioProductStatusFlowReportQuery } from '../../../services/studio/StudioQuery/StudioProductStatusFlowReport/StudioProductStatusFlowReportQuery.services';
import { isValidBarcode } from '../../helper/studio_helper';
import { downloadExcel } from '../../../utils/excelFileHelper';

const CONTROL_HEIGHT = 48;
const KEY_CODE_SPECIAL_CODE_LENGTH = 8;
const KEY_CODE_COLOR_CODE_LENGTH = 3;
const KEY_CODE_LENGTH = KEY_CODE_SPECIAL_CODE_LENGTH + KEY_CODE_COLOR_CODE_LENGTH;
const EXPORT_PAGE_SIZE = 5000;
const EXPORT_CONCURRENCY = 4;
const INPUT_TYPE_BARCODE = '3';
const INPUT_TYPE_SPECIAL_AND_COLOR = '2';
const INPUT_TYPE_KEY_CODE = 'KeyCode';

type SearchMode = 'input' | 'date';

type StudioProductStatusFlowReportRow = {
    id: string;
    barcode: number;
    specialCode: string;
    colorCode: string;
    seasonCode: string;
    line: string;
    productName: string;
    deliveryLocation: number | null;
    deliveryLocationName: string;
    sampleDeliveryDate: string;
    sampleDeliveredBy: string;
    sampleAcceptanceCountry: number | null;
    studioProductAcceptanceDate: string;
    studioProductAcceptanceStaff: string;
    hangingShootDate: string;
    hangingShootStaff: string;
    hangingShootPoseCount: number | null;
    hangingPhotoshopDate: string;
    hangingPhotoshopStaff: string;
    hangingPhotoshopPoseCount: number | null;
    hangingLastStatus: number | null;
    hangingLastStatusName: string;
    modelShootDate: string;
    modelShootStaff: string;
    modelShootPoseCount: number | null;
    modelPhotoshopDate: string;
    modelPhotoshopStaff: string;
    modelPhotoshopPoseCount: number | null;
    modelLastStatus: number | null;
    modelLastStatusName: string;
};

type AppliedFilters = {
    Barcode?: number;
    SpecialCode?: string;
    ColorCode?: string;
    KeyCode?: string;
    AcceptanceDateStart?: string;
    AcceptanceDateEnd?: string;
};

const pick = (item: Record<string, unknown>, camel: string, pascal: string) =>
    (item[camel] ?? item[pascal] ?? '') as string | number | null;

const formatDisplayDate = (value: unknown): string => {
    if (value == null || value === '') {
        return '';
    }

    const date = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const mapRow = (item: Record<string, unknown>, index: number): StudioProductStatusFlowReportRow => {
    const barcode = Number(pick(item, 'barcode', 'Barcode') || 0);
    const specialCode = String(pick(item, 'specialCode', 'SpecialCode') || '');
    const colorCode = String(pick(item, 'colorCode', 'ColorCode') || '');
    const studioProductAcceptanceDate = formatDisplayDate(
        pick(item, 'studioProductAcceptanceDate', 'StudioProductAcceptanceDate')
    );

    return {
        id: `${barcode}-${specialCode}-${colorCode}-${studioProductAcceptanceDate}-${index}`,
        barcode,
        specialCode,
        colorCode,
        seasonCode: String(pick(item, 'seasonCode', 'SeasonCode') || ''),
        line: String(pick(item, 'line', 'Line') || ''),
        productName: String(pick(item, 'productName', 'ProductName') || ''),
        deliveryLocation: (pick(item, 'deliveryLocation', 'DeliveryLocation') as number | null) ?? null,
        deliveryLocationName: String(pick(item, 'deliveryLocationName', 'DeliveryLocationName') || ''),
        sampleDeliveryDate: formatDisplayDate(pick(item, 'sampleDeliveryDate', 'SampleDeliveryDate')),
        sampleDeliveredBy: String(pick(item, 'sampleDeliveredBy', 'SampleDeliveredBy') || ''),
        sampleAcceptanceCountry:
            (pick(item, 'sampleAcceptanceCountry', 'SampleAcceptanceCountry') as number | null) ?? null,
        studioProductAcceptanceDate,
        studioProductAcceptanceStaff: String(
            pick(item, 'studioProductAcceptanceStaff', 'StudioProductAcceptanceStaff') || ''
        ),
        hangingShootDate: formatDisplayDate(pick(item, 'hangingShootDate', 'HangingShootDate')),
        hangingShootStaff: String(pick(item, 'hangingShootStaff', 'HangingShootStaff') || ''),
        hangingShootPoseCount: (pick(item, 'hangingShootPoseCount', 'HangingShootPoseCount') as number | null) ?? null,
        hangingPhotoshopDate: formatDisplayDate(pick(item, 'hangingPhotoshopDate', 'HangingPhotoshopDate')),
        hangingPhotoshopStaff: String(pick(item, 'hangingPhotoshopStaff', 'HangingPhotoshopStaff') || ''),
        hangingPhotoshopPoseCount:
            (pick(item, 'hangingPhotoshopPoseCount', 'HangingPhotoshopPoseCount') as number | null) ?? null,
        hangingLastStatus: (pick(item, 'hangingLastStatus', 'HangingLastStatus') as number | null) ?? null,
        hangingLastStatusName: String(pick(item, 'hangingLastStatusName', 'HangingLastStatusName') || ''),
        modelShootDate: formatDisplayDate(pick(item, 'modelShootDate', 'ModelShootDate')),
        modelShootStaff: String(pick(item, 'modelShootStaff', 'ModelShootStaff') || ''),
        modelShootPoseCount: (pick(item, 'modelShootPoseCount', 'ModelShootPoseCount') as number | null) ?? null,
        modelPhotoshopDate: formatDisplayDate(pick(item, 'modelPhotoshopDate', 'ModelPhotoshopDate')),
        modelPhotoshopStaff: String(pick(item, 'modelPhotoshopStaff', 'ModelPhotoshopStaff') || ''),
        modelPhotoshopPoseCount:
            (pick(item, 'modelPhotoshopPoseCount', 'ModelPhotoshopPoseCount') as number | null) ?? null,
        modelLastStatus: (pick(item, 'modelLastStatus', 'ModelLastStatus') as number | null) ?? null,
        modelLastStatusName: String(pick(item, 'modelLastStatusName', 'ModelLastStatusName') || ''),
    };
};

const toExcelRow = (row: StudioProductStatusFlowReportRow) => ({
    specialCode: row.specialCode,
    colorCode: row.colorCode,
    barcode: row.barcode,
    seasonCode: row.seasonCode,
    line: row.line,
    productName: row.productName,
    deliveryLocationName: row.deliveryLocationName,
    sampleDeliveryDate: row.sampleDeliveryDate,
    sampleDeliveredBy: row.sampleDeliveredBy,
    sampleAcceptanceCountry: row.sampleAcceptanceCountry ?? '',
    studioProductAcceptanceDate: row.studioProductAcceptanceDate,
    studioProductAcceptanceStaff: row.studioProductAcceptanceStaff,
    hangingShootDate: row.hangingShootDate,
    hangingShootStaff: row.hangingShootStaff,
    hangingShootPoseCount: row.hangingShootPoseCount ?? '',
    hangingPhotoshopDate: row.hangingPhotoshopDate,
    hangingPhotoshopStaff: row.hangingPhotoshopStaff,
    hangingPhotoshopPoseCount: row.hangingPhotoshopPoseCount ?? '',
    hangingLastStatusName: row.hangingLastStatusName,
    modelShootDate: row.modelShootDate,
    modelShootStaff: row.modelShootStaff,
    modelShootPoseCount: row.modelShootPoseCount ?? '',
    modelPhotoshopDate: row.modelPhotoshopDate,
    modelPhotoshopStaff: row.modelPhotoshopStaff,
    modelPhotoshopPoseCount: row.modelPhotoshopPoseCount ?? '',
    modelLastStatusName: row.modelLastStatusName,
});

const parseSpecialAndColor = (value: string): { specialCode: string; colorCode: string } | null => {
    const trimmed = value.trim();
    const dashIndex = trimmed.indexOf('-');

    if (dashIndex > 0 && dashIndex < trimmed.length - 1) {
        const specialCode = trimmed.slice(0, dashIndex).trim();
        const colorCode = trimmed.slice(dashIndex + 1).trim();

        if (specialCode && colorCode) {
            return { specialCode, colorCode };
        }
    }

    return null;
};

const parseKeyCode = (value: string): string | null => {
    const trimmed = value.trim();

    if (trimmed.length !== KEY_CODE_LENGTH) {
        return null;
    }

    const specialCode = trimmed.slice(0, KEY_CODE_SPECIAL_CODE_LENGTH);
    const colorCode = trimmed.slice(KEY_CODE_SPECIAL_CODE_LENGTH);

    if (specialCode.length !== KEY_CODE_SPECIAL_CODE_LENGTH || colorCode.length !== KEY_CODE_COLOR_CODE_LENGTH) {
        return null;
    }

    return trimmed;
};

const buildFiltersFromInput = (inputType: string, searchText: string): AppliedFilters | null => {
    const trimmed = searchText.trim();

    if (inputType === INPUT_TYPE_BARCODE) {
        if (!isValidBarcode(trimmed)) {
            return null;
        }
        return { Barcode: Number(trimmed) };
    }

    if (inputType === INPUT_TYPE_SPECIAL_AND_COLOR) {
        const parsed = parseSpecialAndColor(trimmed);
        if (!parsed) {
            return null;
        }
        return { SpecialCode: parsed.specialCode, ColorCode: parsed.colorCode };
    }

    if (inputType === INPUT_TYPE_KEY_CODE) {
        const keyCode = parseKeyCode(trimmed);
        if (!keyCode) {
            return null;
        }
        return { KeyCode: keyCode };
    }

    return null;
};

export const StudioProductStatusFlowReport = () => {
    const { t } = useTranslation();

    const [searchMode, setSearchMode] = useState<SearchMode>('input');
    const [inputType, setInputType] = useState(INPUT_TYPE_BARCODE);
    const [searchText, setSearchText] = useState('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [appliedFilters, setAppliedFilters] = useState<AppliedFilters | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [rows, setRows] = useState<StudioProductStatusFlowReportRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);

    const selectListOptions = useMemo(
        () => [
            { value: INPUT_TYPE_BARCODE, text: t('searchService.barcode') },
            { value: INPUT_TYPE_SPECIAL_AND_COLOR, text: t('searchService.specialAndColorCode') },
            { value: INPUT_TYPE_KEY_CODE, text: t('searchService.keyCode') },
        ],
        [t]
    );

    const searchPlaceholder = useMemo(() => {
        if (inputType === INPUT_TYPE_KEY_CODE) {
            return t('studio.studioProductStatusFlowReport.keyCodePlaceholder');
        }

        if (inputType === INPUT_TYPE_SPECIAL_AND_COLOR) {
            return t('studio.studioProductStatusFlowReport.specialAndColorPlaceholder');
        }

        return t('studio.studioProductStatusFlowReport.searchPlaceholder');
    }, [inputType, t]);

    const isKeyCodeInvalid =
        inputType === INPUT_TYPE_KEY_CODE && searchText.trim().length > 0 && parseKeyCode(searchText) === null;

    const isSearchEnabled = useMemo(() => {
        if (searchMode === 'input') {
            return buildFiltersFromInput(inputType, searchText) !== null;
        }

        if (!startDate || !endDate) {
            return false;
        }
        return new Date(startDate) <= new Date(endDate);
    }, [searchMode, inputType, searchText, startDate, endDate]);

    const fetchData = useCallback(async () => {
        if (!appliedFilters) {
            return;
        }

        try {
            setLoading(true);
            const skipTotalCount = currentPage > 0;
            const response = await StudioProductStatusFlowReportQuery.QueryStudioProductStatusFlowReport({
                ...appliedFilters,
                Page: currentPage + 1,
                PageSize: pageSize,
                SkipTotalCount: skipTotalCount,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().join('\n');
                Swal.fire(errorMessages, '', 'error');
                setRows([]);
                setTotalCount(0);
                return;
            }

            const rawItems = response?.items || response?.Items || [];
            const total = response?.totalCount ?? response?.TotalCount ?? 0;

            setRows(rawItems.map((item: Record<string, unknown>, index: number) => mapRow(item, index)));
            if (!skipTotalCount) {
                setTotalCount(total);
            }
        } catch {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.studioProductStatusFlowReport.fetchError'),
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [appliedFilters, currentPage, pageSize, t]);

    useEffect(() => {
        if (hasSearched && appliedFilters) {
            fetchData();
        }
    }, [hasSearched, appliedFilters, currentPage, pageSize, fetchData]);

    const handleSearchModeChange = (mode: SearchMode) => {
        setSearchMode(mode);
        setSearchText('');
        setStartDate('');
        setEndDate('');
        setHasSearched(false);
        setAppliedFilters(null);
        setRows([]);
        setTotalCount(0);
    };

    const handleSearch = useCallback(() => {
        let filters: AppliedFilters | null = null;

        if (searchMode === 'input') {
            filters = buildFiltersFromInput(inputType, searchText);
        } else if (startDate && endDate) {
            const startStr = startDate.includes('T') ? startDate : `${startDate}T00:00:00`;
            const endStr = endDate.includes('T') ? endDate : `${endDate}T23:59:59`;
            filters = {
                AcceptanceDateStart: startStr,
                AcceptanceDateEnd: endStr,
            };
        }

        if (!filters) {
            return;
        }

        setCurrentPage(0);
        setHasSearched(true);
        setAppliedFilters(filters);
    }, [searchMode, inputType, searchText, startDate, endDate]);

    const handleExcelExport = async () => {
        if (!appliedFilters) {
            return [];
        }

        if (totalCount <= 0) {
            Swal.fire({
                icon: 'info',
                title: t('general.noData'),
                showConfirmButton: false,
                timer: 1500,
            });
            return [];
        }

        const totalPages = Math.max(1, Math.ceil(totalCount / EXPORT_PAGE_SIZE));
        const pageBuckets: Record<string, unknown>[][] = Array.from({ length: totalPages }, () => []);
        let completedPages = 0;

        const updateProgress = () => {
            const content = Swal.getHtmlContainer();
            if (content) {
                content.innerHTML = t('studio.studioProductStatusFlowReport.exportProgress', {
                    current: Math.min(completedPages + 1, totalPages),
                    total: totalPages,
                });
            }
        };

        const fetchExportPage = async (page: number) => {
            const response = await StudioProductStatusFlowReportQuery.QueryStudioProductStatusFlowReport({
                ...appliedFilters,
                Page: page,
                PageSize: EXPORT_PAGE_SIZE,
                SkipTotalCount: page > 1,
            });

            if (response?.errors) {
                const errorMessages = Object.values(response.errors).flat().join('\n');
                throw new Error(errorMessages);
            }

            return {
                page,
                items: (response?.items || response?.Items || []) as Record<string, unknown>[],
            };
        };

        Swal.fire({
            title: t('studio.studioProductStatusFlowReport.exportLoading'),
            html: t('studio.studioProductStatusFlowReport.exportProgress', { current: 1, total: totalPages }),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        try {
            for (let startPage = 1; startPage <= totalPages; startPage += EXPORT_CONCURRENCY) {
                const batchPages = Array.from(
                    { length: Math.min(EXPORT_CONCURRENCY, totalPages - startPage + 1) },
                    (_, index) => startPage + index
                );

                const batchResults = await Promise.all(batchPages.map((page) => fetchExportPage(page)));

                batchResults.forEach(({ page, items }) => {
                    pageBuckets[page - 1] = items;
                    completedPages += 1;
                });

                updateProgress();
            }

            Swal.close();

            const aggregated = pageBuckets.flat();
            const excelRows = aggregated
                .map((item: Record<string, unknown>, index: number) => mapRow(item, index))
                .map(toExcelRow);

            if (!excelRows.length) {
                Swal.fire({
                    icon: 'info',
                    title: t('general.noData'),
                    showConfirmButton: false,
                    timer: 1500,
                });
                return [];
            }

            downloadExcel({
                excelDownloadData: excelRows,
                excelDownloadFolderName: 'StudioProductStatusFlowReport',
                fileNameOverride: 'studio-product-status-flow-report',
            });
        } catch {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('studio.studioProductStatusFlowReport.fetchError'),
            });
        }

        return [];
    };

    const handleChangeDate = (event: any) => {
        setStartDate(event[0] || '');
        setEndDate(event[1] || '');
    };

    const columns = useMemo(
        () => [
            { field: 'specialCode', headerName: t('searchService.specialCode'), width: 120 },
            { field: 'colorCode', headerName: t('studio.colorCode'), width: 100 },
            { field: 'barcode', headerName: t('studio.barcode'), width: 140 },
            { field: 'seasonCode', headerName: t('studio.studioProductStatusFlowReport.seasonCode'), width: 110 },
            { field: 'line', headerName: t('studio.itemCancellationGrid.line'), width: 100 },
            { field: 'productName', headerName: t('studio.productName'), flex: 1, minWidth: 160 },
            {
                field: 'deliveryLocationName',
                headerName: t('studio.studioProductStatusFlowReport.teslimYeriTanim'),
                width: 130,
            },
            {
                field: 'sampleDeliveryDate',
                headerName: t('studio.studioProductStatusFlowReport.numuneTeslimTarihi'),
                width: 160,
            },
            {
                field: 'sampleDeliveredBy',
                headerName: t('studio.studioProductStatusFlowReport.numuneTeslimEden'),
                width: 140,
            },
            {
                field: 'sampleAcceptanceCountry',
                headerName: t('studio.studioProductStatusFlowReport.numuneKabulUlke'),
                width: 150,
            },
            {
                field: 'studioProductAcceptanceDate',
                headerName: t('studio.studioProductStatusFlowReport.studyoUrunKabulTarihi'),
                width: 170,
            },
            {
                field: 'studioProductAcceptanceStaff',
                headerName: t('studio.studioProductStatusFlowReport.studyoUrunKabulPersonel'),
                width: 150,
            },
            {
                field: 'hangingShootDate',
                headerName: t('studio.studioProductStatusFlowReport.askiliCekimTarihi'),
                width: 150,
            },
            {
                field: 'hangingShootStaff',
                headerName: t('studio.studioProductStatusFlowReport.askiliCekimPersonel'),
                width: 140,
            },
            {
                field: 'hangingShootPoseCount',
                headerName: t('studio.studioProductStatusFlowReport.askiliCekimPozSayisi'),
                width: 120,
            },
            {
                field: 'hangingPhotoshopDate',
                headerName: t('studio.studioProductStatusFlowReport.askiliPhotoshopTarihi'),
                width: 150,
            },
            {
                field: 'hangingPhotoshopStaff',
                headerName: t('studio.studioProductStatusFlowReport.askiliPhotoshopPersonel'),
                width: 150,
            },
            {
                field: 'hangingPhotoshopPoseCount',
                headerName: t('studio.studioProductStatusFlowReport.askiliPhotoshopPozSayisi'),
                width: 130,
            },
            {
                field: 'hangingLastStatusName',
                headerName: t('studio.studioProductStatusFlowReport.askiliSonStatuTanim'),
                width: 150,
            },
            {
                field: 'modelShootDate',
                headerName: t('studio.studioProductStatusFlowReport.modelliCekimTarihi'),
                width: 150,
            },
            {
                field: 'modelShootStaff',
                headerName: t('studio.studioProductStatusFlowReport.modelliCekimPersonel'),
                width: 140,
            },
            {
                field: 'modelShootPoseCount',
                headerName: t('studio.studioProductStatusFlowReport.modelliCekimPozSayisi'),
                width: 130,
            },
            {
                field: 'modelPhotoshopDate',
                headerName: t('studio.studioProductStatusFlowReport.modelliPhotoshopTarihi'),
                width: 160,
            },
            {
                field: 'modelPhotoshopStaff',
                headerName: t('studio.studioProductStatusFlowReport.modelliPhotoshopPersonel'),
                width: 150,
            },
            {
                field: 'modelPhotoshopPoseCount',
                headerName: t('studio.studioProductStatusFlowReport.modelliPhotoshopPozSayisi'),
                width: 140,
            },
            {
                field: 'modelLastStatusName',
                headerName: t('studio.studioProductStatusFlowReport.modelliSonStatuTanim'),
                width: 160,
            },
        ],
        [t]
    );

    const breadcrumbList = [
        { label: t('general.home'), path: '/home' },
        { label: t('studio.title'), path: '/studio' },
        { label: t('studio.studioProductStatusFlowReport.title') },
    ];

    return (
        <SCUIContent>
            {loading && <SCUILoadingLinear />}
            <SCUIBreadCrumb items={breadcrumbList} />

            <Box sx={{ p: 2 }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '14px',
                        border: '1px solid #EDEFF2',
                        backgroundColor: '#FAFAFA',
                        p: { xs: 2, sm: 2.5 },
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
                        <AssessmentOutlinedIcon color="primary" />
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
                            {t('studio.studioProductStatusFlowReport.title')}
                        </Typography>
                    </Stack>

                    <FormControl sx={{ mb: 2 }}>
                        <RadioGroup
                            row
                            value={searchMode}
                            onChange={(e) => handleSearchModeChange(e.target.value as SearchMode)}
                        >
                            <FormControlLabel
                                value="input"
                                control={<Radio />}
                                label={t('studio.studioProductStatusFlowReport.searchByProduct')}
                            />
                            <FormControlLabel
                                value="date"
                                control={<Radio />}
                                label={t('studio.studioProductStatusFlowReport.searchByAcceptanceDate')}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', lg: 'center' }}
                    >
                        {searchMode === 'input' ? (
                            <>
                                <FormControl sx={{ minWidth: 220 }}>
                                    <Select
                                        value={inputType}
                                        onChange={(e) => {
                                            setInputType(e.target.value);
                                            setSearchText('');
                                        }}
                                        sx={{ height: CONTROL_HEIGHT }}
                                    >
                                        {selectListOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.text}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder={searchPlaceholder}
                                    fullWidth
                                    error={isKeyCodeInvalid}
                                    helperText={
                                        isKeyCodeInvalid
                                            ? t('studio.studioProductStatusFlowReport.invalidKeyCode')
                                            : undefined
                                    }
                                    sx={{ maxWidth: 420 }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && isSearchEnabled) {
                                            handleSearch();
                                        }
                                    }}
                                    slotProps={{
                                        htmlInput: {
                                            maxLength:
                                                inputType === INPUT_TYPE_KEY_CODE ? KEY_CODE_LENGTH : undefined,
                                        },
                                        input: {
                                            sx: { height: CONTROL_HEIGHT },
                                        },
                                    }}
                                />
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SCUIDateRangerV2
                                    fromDate={startDate}
                                    toDate={endDate}
                                    setDateRange={handleChangeDate}
                                    dateFormat="YYYY-MM-DD"
                                    size="small"
                                    containerStyling={{
                                        width: '280px',
                                        '& .MuiFormControl-root': { margin: 0 },
                                        '& .MuiFormLabel-root': { display: 'none' },
                                        '& .MuiOutlinedInput-root': {
                                            height: `${CONTROL_HEIGHT}px`,
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '12px 14px',
                                            boxSizing: 'border-box',
                                        },
                                    }}
                                />
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            startIcon={<SearchOutlinedIcon />}
                            disabled={!isSearchEnabled}
                            onClick={handleSearch}
                            sx={{
                                height: CONTROL_HEIGHT,
                                minHeight: CONTROL_HEIGHT,
                                maxHeight: CONTROL_HEIGHT,
                                minWidth: 120,
                                borderRadius: '10px',
                                textTransform: 'none',
                                alignSelf: { xs: 'stretch', lg: 'center' },
                            }}
                        >
                            {t('general.search')}
                        </Button>
                    </Stack>
                </Paper>

                {hasSearched && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: '14px',
                            border: '1px solid #EDEFF2',
                            backgroundColor: '#FAFAFA',
                            p: { xs: 2, sm: 2.5 },
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#EFF6FF',
                                        color: '#2563EB',
                                        border: '1px solid #BFDBFE',
                                    }}
                                >
                                    <ListAltOutlinedIcon sx={{ fontSize: '1.15rem' }} />
                                </Box>
                                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827' }}>
                                    {t('studio.studioProductStatusFlowReport.gridTitle')}
                                </Typography>
                            </Stack>

                            <SCUIDownloadButton
                                position="right"
                                onclick={handleExcelExport}
                                excelData={{
                                    excelDownloadData: [],
                                    excelDownloadFolderName: 'StudioProductStatusFlowReport',
                                    excelDownloadButtonText: t('general.downloadExcel'),
                                }}
                            />
                        </Stack>

                        {rows.length === 0 && !loading ? (
                            <SCUINoRecordComponent text={t('general.noDataFound')} />
                        ) : (
                            <SCUICustomDataGrid
                                rows={rows}
                                columns={columns}
                                pageSize={pageSize}
                                onPageSizeChange={setPageSize}
                                page={currentPage}
                                onPageChange={setCurrentPage}
                                rowCount={totalCount}
                                getRowId={(row: StudioProductStatusFlowReportRow) => row.id}
                                paginationModeChange
                                loading={loading}
                            />
                        )}
                    </Paper>
                )}
            </Box>
        </SCUIContent>
    );
};
